import React, { useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import { serverTimestamp } from 'firebase/firestore';
import { sendBookingEmail } from '../services/emailService';

const BookingModal = () => {
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    location: '',
    vehicleNo: '',
    vehicleModel: '',
    kms: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Please enter your name');
      return false;
    }
    if (!formData.mobile.trim()) {
      setError('Please enter your mobile number');
      return false;
    }
    if (!/^[0-9]{10,15}$/.test(formData.mobile.replace(/\s+/g, ''))) {
      setError('Please enter a valid mobile number (10-15 digits)');
      return false;
    }
    if (!formData.email.trim()) {
      setError('Please enter your email address');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (!formData.location.trim()) {
      setError('Please enter your location');
      return false;
    }
    if (!formData.vehicleNo.trim()) {
      setError('Please enter your vehicle number');
      return false;
    }
    if (!formData.vehicleModel.trim()) {
      setError('Please enter your vehicle model');
      return false;
    }
    if (!formData.kms.trim()) {
      setError('Please enter the KM\'s');
      return false;
    }
    if (!/^\d+$/.test(formData.kms.trim()) || Number(formData.kms.trim()) <= 0) {
      setError('Please enter a valid KM reading');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      // Store data in Firestore
      const bookingData = {
        name: formData.name.trim(),
        mobile: formData.mobile.trim(),
        email: formData.email.trim().toLowerCase(),
        location: formData.location.trim(),
        vehicleNo: formData.vehicleNo.trim().toUpperCase(),
        vehicleModel: formData.vehicleModel.trim(),
        kms: formData.kms.trim(),
        status: 'new',
        createdAt: new Date().toISOString(),
        timestamp: serverTimestamp(),
      };

      // Add document to Firestore
      // const docRef = await addDoc(collection(db, 'bookings'), bookingData);
      
      // console.log('Booking saved with ID: ', docRef.id);

      // Send email notification using EmailJS
      try {
        await sendBookingEmail(bookingData);
        console.log('Email sent successfully');
      } catch (emailError) {
        console.error('Email sending failed:', emailError);
        // Don't fail the booking if email fails - booking is already saved
      }

      setSuccess(true);
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({
          name: '',
          mobile: '',
          email: '',
          location: '',
          vehicleNo: '',
          vehicleModel: '',
          kms: ''
        });
        setSuccess(false);
      }, 3000);

    } catch (err) {
      console.error('Error saving booking: ', err);
      setError('Failed to submit booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      id="contact_us"
      data-booking-section
      sx={{
        position: 'relative',
        background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
        padding: { xs: '60px 20px', sm: '80px 40px', md: '100px 80px' },
        minHeight: { xs: 'auto', md: '600px' },
        display: 'flex',
        overflow: 'hidden',
        alignItems: 'center',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'radial-gradient(circle at 85% 15%, rgba(255, 111, 0, 0.12) 0%, transparent 45%)',
          pointerEvents: 'none'
        }
      }}
    >
      <Container maxWidth="xl" sx={{ width: '100%', position: 'relative', zIndex: 1 }}>
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: { xs: 'center', md: 'flex-start' },
                alignItems: 'center',
                height: '100%',
                paddingTop: { xs: '20px', md: '0' }
              }}
            >
              <Box
                component="img"
                src="https://images.unsplash.com/photo-1555215695-3004980ad54e?w=800&auto=format&fit=crop"
                alt="Luxury Car"
                sx={{
                  width: '100%',
                  maxWidth: { xs: '100%', md: '550px', lg: '600px' },
                  height: 'auto',
                  borderRadius: '16px',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.02)'
                  }
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '28px',
                maxWidth: { xs: '100%', md: '500px' },
                marginLeft: { xs: '0', md: 'auto' }
              }}
            >
              <Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: '32px', sm: '36px', md: '42px', lg: '48px' },
                    fontWeight: 'bold',
                    color: 'white',
                    marginBottom: '12px',
                    lineHeight: 1.2
                  }}
                >
                  Book Your Service
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { xs: '15px', md: '16px' },
                    color: 'white',
                    opacity: 0.9,
                    lineHeight: 1.5
                  }}
                >
                  Fill out the form below and we'll get back to you as soon as possible to schedule your service.
                </Typography>
              </Box>

              {error && (
                <Alert 
                  severity="error" 
                  sx={{ 
                    borderRadius: '8px',
                    '& .MuiAlert-message': {
                      color: '#d32f2f'
                    }
                  }}
                >
                  {error}
                </Alert>
              )}

              {success && (
                <Alert 
                  severity="success" 
                  sx={{ 
                    borderRadius: '8px',
                    '& .MuiAlert-message': {
                      color: '#2e7d32'
                    }
                  }}
                >
                  Booking submitted successfully! We'll contact you soon.
                </Alert>
              )}
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {/* Full Name Field */}
                <Box>
                  <Typography
                    sx={{
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: 500,
                      marginBottom: '8px',
                      display: 'block'
                    }}
                  >
                    Full Name
                  </Typography>
                  <TextField
                    fullWidth
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    variant="outlined"
                    disabled={loading || success}
                    placeholder="Enter your full name"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        transition: 'all 0.3s ease',
                        '& fieldset': {
                          borderColor: '#ddd',
                          borderWidth: '1.5px'
                        },
                        '&:hover fieldset': {
                          borderColor: '#ff6f00',
                          boxShadow: '0 0 0 3px rgba(255, 111, 0, 0.1)'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#ff6f00',
                          borderWidth: '2px',
                          boxShadow: '0 0 0 4px rgba(255, 111, 0, 0.15)'
                        }
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: '14px 18px',
                        fontSize: '16px',
                        color: '#333',
                        '&::placeholder': {
                          color: '#999',
                          opacity: 1
                        }
                      }
                    }}
                  />
                </Box>

                {/* Mobile Number Field */}
                <Box>
                  <Typography
                    sx={{
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: 500,
                      marginBottom: '8px',
                      display: 'block'
                    }}
                  >
                    Mobile Number
                  </Typography>
                  <TextField
                    fullWidth
                    name="mobile"
                    type="tel"
                    value={formData.mobile}
                    onChange={handleChange}
                    variant="outlined"
                    disabled={loading || success}
                    placeholder="10-15 digits"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        transition: 'all 0.3s ease',
                        '& fieldset': {
                          borderColor: '#ddd',
                          borderWidth: '1.5px'
                        },
                        '&:hover fieldset': {
                          borderColor: '#ff6f00',
                          boxShadow: '0 0 0 3px rgba(255, 111, 0, 0.1)'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#ff6f00',
                          borderWidth: '2px',
                          boxShadow: '0 0 0 4px rgba(255, 111, 0, 0.15)'
                        }
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: '14px 18px',
                        fontSize: '16px',
                        color: '#333',
                        '&::placeholder': {
                          color: '#999',
                          opacity: 1
                        }
                      }
                    }}
                  />
                </Box>

                {/* Email Field */}
                <Box>
                  <Typography
                    sx={{
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: 500,
                      marginBottom: '8px',
                      display: 'block'
                    }}
                  >
                    Email Address
                  </Typography>
                  <TextField
                    fullWidth
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    variant="outlined"
                    disabled={loading || success}
                    placeholder="your.email@example.com"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        transition: 'all 0.3s ease',
                        '& fieldset': {
                          borderColor: '#ddd',
                          borderWidth: '1.5px'
                        },
                        '&:hover fieldset': {
                          borderColor: '#ff6f00',
                          boxShadow: '0 0 0 3px rgba(255, 111, 0, 0.1)'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#ff6f00',
                          borderWidth: '2px',
                          boxShadow: '0 0 0 4px rgba(255, 111, 0, 0.15)'
                        }
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: '14px 18px',
                        fontSize: '16px',
                        color: '#333',
                        '&::placeholder': {
                          color: '#999',
                          opacity: 1
                        }
                      }
                    }}
                  />
                </Box>

                {/* Location Field */}
                <Box>
                  <Typography
                    sx={{
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: 500,
                      marginBottom: '8px',
                      display: 'block'
                    }}
                  >
                    Location
                  </Typography>
                  <TextField
                    fullWidth
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    variant="outlined"
                    disabled={loading || success}
                    placeholder="City, State"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        transition: 'all 0.3s ease',
                        '& fieldset': {
                          borderColor: '#ddd',
                          borderWidth: '1.5px'
                        },
                        '&:hover fieldset': {
                          borderColor: '#ff6f00',
                          boxShadow: '0 0 0 3px rgba(255, 111, 0, 0.1)'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#ff6f00',
                          borderWidth: '2px',
                          boxShadow: '0 0 0 4px rgba(255, 111, 0, 0.15)'
                        }
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: '14px 18px',
                        fontSize: '16px',
                        color: '#333',
                        '&::placeholder': {
                          color: '#999',
                          opacity: 1
                        }
                      }
                    }}
                  />
                </Box>

                {/* Vehicle Model Field */}
                <Box>
                  <Typography
                    sx={{
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: 500,
                      marginBottom: '8px',
                      display: 'block'
                    }}
                  >
                    Vehicle Model
                  </Typography>
                  <TextField
                    fullWidth
                    name="vehicleModel"
                    value={formData.vehicleModel}
                    onChange={handleChange}
                    variant="outlined"
                    disabled={loading || success}
                    placeholder="e.g. Honda City, Maruti Swift"
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        transition: 'all 0.3s ease',
                        '& fieldset': {
                          borderColor: '#ddd',
                          borderWidth: '1.5px'
                        },
                        '&:hover fieldset': {
                          borderColor: '#ff6f00',
                          boxShadow: '0 0 0 3px rgba(255, 111, 0, 0.1)'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#ff6f00',
                          borderWidth: '2px',
                          boxShadow: '0 0 0 4px rgba(255, 111, 0, 0.15)'
                        }
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: '14px 18px',
                        fontSize: '16px',
                        color: '#333',
                        '&::placeholder': {
                          color: '#999',
                          opacity: 1
                        }
                      }
                    }}
                  />
                </Box>

                {/* Vehicle No Field */}
                <Box>
                  <Typography
                    sx={{
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: 500,
                      marginBottom: '8px',
                      display: 'block'
                    }}
                  >
                    Vehicle No
                  </Typography>
                  <TextField
                    fullWidth
                    name="vehicleNo"
                    value={formData.vehicleNo}
                    onChange={handleChange}
                    variant="outlined"
                    disabled={loading || success}
                    placeholder="e.g. TN 01 AB 1234"
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        transition: 'all 0.3s ease',
                        '& fieldset': {
                          borderColor: '#ddd',
                          borderWidth: '1.5px'
                        },
                        '&:hover fieldset': {
                          borderColor: '#ff6f00',
                          boxShadow: '0 0 0 3px rgba(255, 111, 0, 0.1)'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#ff6f00',
                          borderWidth: '2px',
                          boxShadow: '0 0 0 4px rgba(255, 111, 0, 0.15)'
                        }
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: '14px 18px',
                        fontSize: '16px',
                        color: '#333',
                        '&::placeholder': {
                          color: '#999',
                          opacity: 1
                        }
                      }
                    }}
                  />
                </Box>

                {/* KM's Field */}
                <Box>
                  <Typography
                    sx={{
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: 500,
                      marginBottom: '8px',
                      display: 'block'
                    }}
                  >
                    KM&apos;s
                  </Typography>
                  <TextField
                    fullWidth
                    name="kms"
                    type="number"
                    value={formData.kms}
                    onChange={handleChange}
                    variant="outlined"
                    disabled={loading || success}
                    placeholder="Current odometer reading"
                    required
                    inputProps={{ min: 1 }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        transition: 'all 0.3s ease',
                        '& fieldset': {
                          borderColor: '#ddd',
                          borderWidth: '1.5px'
                        },
                        '&:hover fieldset': {
                          borderColor: '#ff6f00',
                          boxShadow: '0 0 0 3px rgba(255, 111, 0, 0.1)'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#ff6f00',
                          borderWidth: '2px',
                          boxShadow: '0 0 0 4px rgba(255, 111, 0, 0.15)'
                        }
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: '14px 18px',
                        fontSize: '16px',
                        color: '#333',
                        '&::placeholder': {
                          color: '#999',
                          opacity: 1
                        }
                      }
                    }}
                  />
                </Box>
              </Box>
              
              <Button
                type="submit"
                variant="contained"
                disabled={loading || success}
                sx={{
                  background: success 
                    ? 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)'
                    : 'linear-gradient(135deg, #ff6f00 0%, #ff8f00 100%)',
                  color: 'white',
                  padding: '14px 36px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  borderRadius: '8px',
                  textTransform: 'none',
                  width: 'fit-content',
                  marginTop: '8px',
                  boxShadow: success
                    ? '0 4px 12px rgba(46, 125, 50, 0.3)'
                    : '0 4px 12px rgba(255, 111, 0, 0.3)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                    transition: 'left 0.5s'
                  },
                  '&:hover': {
                    background: success
                      ? 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)'
                      : 'linear-gradient(135deg, #ff8f00 0%, #ff6f00 100%)',
                    boxShadow: success
                      ? '0 6px 20px rgba(46, 125, 50, 0.4)'
                      : '0 6px 20px rgba(255, 111, 0, 0.4)',
                    transform: 'translateY(-2px)',
                    '&::before': {
                      left: '100%'
                    }
                  },
                  '&:active': {
                    transform: 'translateY(0px)'
                  },
                  '&:disabled': {
                    background: '#ccc',
                    boxShadow: 'none'
                  }
                }}
              >
                {loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CircularProgress size={20} sx={{ color: 'white' }} />
                    <span>Submitting...</span>
                  </Box>
                ) : success ? (
                  'Submitted Successfully!'
                ) : (
                  'Submit Booking'
                )}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default BookingModal;
